import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { useLocale } from '@/shared/lib/i18n/locale-context';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { REGION_NAMES, citiesOf } from '@/shared/config/saudi-regions';
import { SubMissionKind } from '@/shared/api/generated/model';
import type { CreateSubMissionCommand } from '@/shared/api/generated/model';

const inputClass =
  'w-full rounded-md border border-border bg-surface-elevated px-3 py-2 text-start outline-none focus:border-primary';

/**
 * Opens registration for a sub-mission — Hajj, Umrah, an exhibition, an event, a lecture (§23.3).
 *
 * ⚠️ **This family is CREATED, not reported.** The other two begin with somebody in trouble filling
 * a form; this one begins with the association deciding to run something and asking who will come.
 * So there is no photograph, no incident time, no committee — and the two questions a volunteer will
 * ask before registering, «كم مقعدًا» and «متى», are the two this form exists to answer.
 */
export function CreateSubMissionDialog({
  open,
  onOpenChange,
  onConfirm,
  busy,
  serverError,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (command: CreateSubMissionCommand) => void;
  busy: boolean;
  serverError?: string | null;
}) {
  const { t } = useLocale();

  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<SubMissionKind>(SubMissionKind.Exhibition);
  const [region, setRegion] = useState<string>(REGION_NAMES[0]!);
  const [city, setCity] = useState<string>(citiesOf(REGION_NAMES[0]!)[0]!);
  const [startsAt, setStartsAt] = useState('');
  const [capacity, setCapacity] = useState('');
  const [longitude, setLongitude] = useState('');
  const [latitude, setLatitude] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setTitle('');
    setStartsAt('');
    setCapacity('');
    setLongitude('');
    setLatitude('');
    setDescription('');
    setError(null);
  };

  const submit = () => {
    const trimmed = title.trim();

    if (!trimmed) {
      setError(t.missions.subTitleRequired);
      return;
    }

    if (!startsAt) {
      setError(t.missions.subWhenRequired);
      return;
    }

    // ⚠️ **Parsed as LOCAL time and sent as an instant.** `datetime-local` gives «2026-09-21T09:00»
    // with no zone; an operator typing nine in the morning means nine where they are standing, and
    // sending the text unqualified would have the server read it as UTC — three hours out, in the
    // direction that opens registration for an event that has already begun.
    const when = new Date(startsAt);

    if (Number.isNaN(when.getTime()) || when.getTime() <= Date.now()) {
      setError(t.missions.subWhenPast);
      return;
    }

    const lon = Number(longitude);
    const lat = Number(latitude);

    if (!Number.isFinite(lon) || !Number.isFinite(lat) || (lon === 0 && lat === 0)) {
      setError(t.missions.subWhereRequired);
      return;
    }

    // ⚠️ Blank means UNLIMITED, which is a real answer — a lecture in a hall the association does
    // not own has no number to give. It must not become zero.
    const seats = capacity.trim() === '' ? null : Number(capacity);

    if (seats !== null && (!Number.isInteger(seats) || seats < 1)) {
      setError(t.missions.subSeatsInvalid);
      return;
    }

    onConfirm({
      title: trimmed,
      kind,
      region,
      city,
      longitude: lon,
      latitude: lat,
      startsAt: when.toISOString(),
      capacity: seats,
      description: description.trim() === '' ? null : description.trim(),
    });

    reset();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent closeLabel={t.missions.closeAria}>
        <DialogHeader>
          <DialogTitle>{t.missions.subCreateTitle}</DialogTitle>
          <DialogDescription>{t.missions.subCreateLede}</DialogDescription>
        </DialogHeader>

        <Field label={t.missions.subTitleLabel} htmlFor="sub-title">
          <input
            id="sub-title"
            autoFocus
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
              setError(null);
            }}
            placeholder={t.missions.subTitlePlaceholder}
            className={inputClass}
          />
        </Field>

        <Field label={t.missions.subKindLabel} htmlFor="sub-kind">
          <select
            id="sub-kind"
            value={kind}
            onChange={(event) => setKind(event.target.value as SubMissionKind)}
            className={inputClass}
          >
            {Object.values(SubMissionKind).map((value) => (
              <option key={value} value={value}>
                {t.missions.subKinds[value]}
              </option>
            ))}
          </select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          {/* ⚠️ A dropdown, not a text box. The region is matched character for character against a
              volunteer's own (§23.4); a typed «الجوف » with a trailing space opens a registration
              nobody in Al-Jouf is ever told about. */}
          <Field label={t.missions.subRegionLabel} htmlFor="sub-region">
            <select
              id="sub-region"
              value={region}
              onChange={(event) => {
                setRegion(event.target.value);
                setCity(citiesOf(event.target.value)[0]!);
              }}
              className={inputClass}
            >
              {REGION_NAMES.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </Field>

          <Field label={t.missions.subCityLabel} htmlFor="sub-city">
            <select
              id="sub-city"
              value={city}
              onChange={(event) => setCity(event.target.value)}
              className={inputClass}
            >
              {citiesOf(region).map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t.missions.subWhenLabel} htmlFor="sub-when">
            <input
              id="sub-when"
              type="datetime-local"
              value={startsAt}
              onChange={(event) => {
                setStartsAt(event.target.value);
                setError(null);
              }}
              className={inputClass}
            />
          </Field>

          <Field label={t.missions.subSeatsLabel} htmlFor="sub-seats" hint={t.missions.subSeatsHint}>
            <input
              id="sub-seats"
              type="number"
              min={1}
              value={capacity}
              onChange={(event) => {
                setCapacity(event.target.value);
                setError(null);
              }}
              className={inputClass}
            />
          </Field>
        </div>

        {/* ⚠️ Latitude and longitude as two named boxes rather than one «lat, lon» string. The pair
            is read off a map and typed by hand, and a single field invites the two to be swapped —
            which puts an exhibition in the Arabian Sea with no error anywhere, because both numbers
            are valid coordinates. */}
        <div className="grid grid-cols-2 gap-3">
          <Field label={t.missions.subLatitudeLabel} htmlFor="sub-lat">
            <input
              id="sub-lat"
              inputMode="decimal"
              dir="ltr"
              value={latitude}
              onChange={(event) => {
                setLatitude(event.target.value);
                setError(null);
              }}
              className={inputClass}
            />
          </Field>

          <Field label={t.missions.subLongitudeLabel} htmlFor="sub-lon">
            <input
              id="sub-lon"
              inputMode="decimal"
              dir="ltr"
              value={longitude}
              onChange={(event) => {
                setLongitude(event.target.value);
                setError(null);
              }}
              className={inputClass}
            />
          </Field>
        </div>

        <Field label={t.missions.subDescriptionLabel} htmlFor="sub-description">
          <textarea
            id="sub-description"
            rows={3}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            className={inputClass}
          />
        </Field>

        {error ?? serverError ? (
          <p role="alert" className="text-sm text-danger">
            {error ?? serverError}
          </p>
        ) : null}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t.missions.cancel}
          </Button>
          <Button disabled={busy} onClick={submit}>
            {t.missions.subCreateConfirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
